export const DEFAULT_LANGUAGE = 'ua';
export const SUPPORTED_LANGUAGES = ['ua', 'ru', 'en'];
export const LANGUAGE_STORAGE_KEY = 'uiLanguage';
export const AUTH_LANGUAGE_STORAGE_KEY = 'lastAuthLanguage';

const LANGUAGE_PREFIXES = {
  ua: '/ua',
  ru: '/ru',
  en: '/en',
};

const ensureLeadingSlash = (path = '/') => (path.startsWith('/') ? path : `/${path}`);

export const detectLanguageFromPath = (pathname = '/') => {
  const normalizedPath = ensureLeadingSlash(pathname);
  const match = normalizedPath.match(/^\/(ua|ru|en)(\/|$)/);
  return match ? match[1] : null;
};

export const resolveLanguageFromParam = (languageParam, fallback = DEFAULT_LANGUAGE) =>
  SUPPORTED_LANGUAGES.includes(languageParam) ? languageParam : fallback;

export const resolveLanguagePriority = (...languageCandidates) => {
  for (const candidate of languageCandidates) {
    const resolved = resolveLanguageFromParam(candidate, null);
    if (resolved) {
      return resolved;
    }
  }

  return DEFAULT_LANGUAGE;
};

export const getLanguagePrefix = (language) => LANGUAGE_PREFIXES[language] ?? '';

export const stripLanguagePrefix = (pathname = '/') => {
  const normalizedPath = ensureLeadingSlash(pathname);
  const language = detectLanguageFromPath(normalizedPath);
  const prefix = getLanguagePrefix(language);

  if (!prefix || !normalizedPath.startsWith(prefix)) {
    return normalizedPath;
  }

  const trimmedPath = normalizedPath.slice(prefix.length) || '/';
  return trimmedPath.startsWith('/') ? trimmedPath : `/${trimmedPath}`;
};

export const buildLocalizedPath = (path = '/', language = DEFAULT_LANGUAGE) => {
  const normalizedPath = ensureLeadingSlash(path);
  const sanitizedPath = stripLanguagePrefix(normalizedPath);
  const prefix = getLanguagePrefix(language);

  if (!prefix) {
    return sanitizedPath || '/';
  }

  if (sanitizedPath === '/' || sanitizedPath === '') {
    return prefix;
  }

  return `${prefix}${sanitizedPath}`;
};

export const buildLocalizedUrl = (
  pathname = '/',
  language = DEFAULT_LANGUAGE,
  search = '',
  hash = '',
) => {
  const localizedPath = buildLocalizedPath(pathname, language);
  return `${localizedPath}${search || ''}${hash || ''}`;
};

export const getStoredLanguage = () => {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored) {
      return resolveLanguageFromParam(stored);
    }
  } catch (error) {
    console.warn('Не удалось прочитать язык из localStorage', error);
  }
  return null;
};

export const persistLanguage = (language) => {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch (error) {
    console.warn('Не удалось сохранить язык в localStorage', error);
  }
};

export const persistAuthLanguage = (language) => {
  try {
    const payload = JSON.stringify({ value: language, ts: Date.now() });
    localStorage.setItem(AUTH_LANGUAGE_STORAGE_KEY, payload);
  } catch (error) {
    console.warn('Не удалось сохранить язык авторизации', error);
  }
};

export const getFreshAuthLanguage = (maxAgeMs = 10 * 60 * 1000) => {
  try {
    const raw = localStorage.getItem(AUTH_LANGUAGE_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    const value = resolveLanguageFromParam(parsed?.value);
    const timestamp = typeof parsed?.ts === 'number' ? parsed.ts : 0;

    if (!value) {
      return null;
    }

    if (timestamp && Date.now() - timestamp > maxAgeMs) {
      return null;
    }

    return value;
  } catch (error) {
    console.warn('Не удалось прочитать язык авторизации', error);
    return null;
  }
};

export const initializeLanguageFromPath = (i18nInstance, pathname = '/') => {
  const languageFromPath = detectLanguageFromPath(pathname) || DEFAULT_LANGUAGE;
  if (i18nInstance?.language !== languageFromPath) {
    console.debug('[LANG-CHANGE]', {
      from: i18nInstance?.language,
      to: languageFromPath,
      pathname,
    });
    i18nInstance?.changeLanguage(languageFromPath);
  }
  return languageFromPath;
};

export const resolveThanksPageLanguageFromSearch = (search = '', fallback = DEFAULT_LANGUAGE) => {
  const params = new URLSearchParams(search || '');
  const rawLanguage = (params.get('language') || '').trim().toLowerCase();

  if (rawLanguage === 'uk' || rawLanguage === 'ua') {
    return 'ua';
  }

  if (rawLanguage === 'ru' || rawLanguage === 'en') {
    return rawLanguage;
  }

  return fallback;
};

export const initializeLanguage = (i18nInstance, pathname = '/', search = '') => {
  const isThanksForPaymentPage = pathname.includes('/thanks-for-the-payment');
  const language = isThanksForPaymentPage
    ? resolveThanksPageLanguageFromSearch(search, detectLanguageFromPath(pathname) || DEFAULT_LANGUAGE)
    : detectLanguageFromPath(pathname) || DEFAULT_LANGUAGE;

  if (i18nInstance?.language !== language) {
    i18nInstance?.changeLanguage(language);
  }

  return language;
};
